/**
 * Level data schema.
 *
 * Levels are pure JSON so the kids (and agents) can author them without writing
 * code, and so the in-game editor can round-trip them to disk. This file is the
 * single source of truth for the shape; `validateLevel` is run both by the
 * loader at runtime and by `npm run validate:levels` in CI.
 *
 * COORDINATE CONVENTIONS - these differ between geometry and props on purpose:
 *   - Geometry boxes use CENTER + SIZE, because that is how you think about a wall.
 *   - Props use a BASE position (feet on the surface), matching the importer's
 *     "origin at the base" normalization. So a vase on a 0.4m table sits at y=0.4.
 * Getting these mixed up is the most likely authoring mistake, so the validator
 * checks for props floating in mid-air or buried in the floor.
 */

export type Vec3Tuple = [number, number, number];
export type Vec2Tuple = [number, number];

export interface RoomDef {
  id: string;
  name: string;
  /** Axis-aligned XZ rectangle. Used to answer "which room is granny in?" */
  min: Vec2Tuple;
  max: Vec2Tuple;
}

export type GeometryKind = 'floor' | 'wall' | 'furniture' | 'ceiling';

export interface GeometryDef {
  id: string;
  kind: GeometryKind;
  /** Box center. */
  position: Vec3Tuple;
  size: Vec3Tuple;
  rotationY?: number;
  /** Blocks granny's line of sight. Walls and tall furniture: true. Rugs: false. */
  sightBlocker?: boolean;
  /**
   * Only the cat can pass through/under this. Excluded from granny's navmesh.
   * This is the mechanic that makes her being faster than you survivable.
   */
  catOnly?: boolean;
  /** Cat can climb this surface (unlocked at L2). */
  climbable?: boolean;
  color?: string;
}

export interface PropDef {
  id: string;
  /** Asset id, resolved through data/assets.manifest.json. */
  asset: string;
  /** BASE position - the point the object rests on. */
  position: Vec3Tuple;
  rotationY?: number;
  /** Static props never move. Dynamic props are real rigidbodies. */
  dynamic?: boolean;
  /** Mischief action id fired when this prop is knocked off / broken. */
  mischief?: string;
  /** Shatters into pieces above this impact impulse. */
  breakable?: boolean;
  /** Cat can pick this up and carry it (heist objectives). */
  carryable?: boolean;
  /** Overrides the manifest's mass. */
  mass?: number;
  /**
   * Whether this prop gets a collider at all. Defaults to true.
   *
   * Set false for props the cat needs to be INSIDE - an open cardboard box is a
   * hide spot, and a solid cuboid placeholder would shove the cat straight back
   * out of it. Also correct for purely decorative dressing.
   */
  solid?: boolean;
}

export interface ActivityDef {
  /** Must match an activity implemented in src/ai/routine.ts. */
  activityId: string;
  /** Where granny stands to do it. */
  anchor: Vec3Tuple;
  /** What she faces while doing it. Defaults to the room center. */
  faceTowards?: Vec3Tuple;
  durationSeconds: number;
  /** Relative likelihood of being chosen next. */
  weight?: number;
}

export interface GrannyDef {
  spawn: Vec3Tuple;
  /** Weighted schedule of activities. See DESIGN.md 1.4 - this IS the level design. */
  routine: ActivityDef[];
  /** Which key she carries in her apron, if any. */
  carriesKey?: string;
}

export interface HideSpotDef {
  id: string;
  label: string;
  position: Vec3Tuple;
  /** Trigger volume the cat must be inside to count as hidden. */
  size: Vec3Tuple;
}

export type CatMomentKind =
  | 'sunbeam_nap'
  | 'sit_in_box'
  | 'knock_pen'
  | 'stare_at_wall'
  | 'chase_dust'
  | 'drink_tap'
  | 'scratch_post';

export interface CatMomentDef {
  id: string;
  kind: CatMomentKind;
  position: Vec3Tuple;
  cozyPoints?: number;
}

export type KeyFlavor = 'plain' | 'mischief' | 'granny';

export interface KeyDef {
  id: string;
  flavor: KeyFlavor;
  /** Where it sits. Omitted for 'granny' keys, which ride in her apron. */
  position?: Vec3Tuple;
  /** For 'mischief' keys: the prop id whose destruction reveals this key. */
  revealedBy?: string;
}

export type ObjectiveKind = 'mischief' | 'heist' | 'discovery';

export interface ObjectiveDef {
  id: string;
  kind: ObjectiveKind;
  /** Short, icon-friendly text. Ages 6-9 - keep it under about six words. */
  label: string;
  target: number;
  /** For 'mischief': counts matching mischief action ids, or any if omitted. */
  matchActions?: string[];
  /** For 'heist': the carryable prop and where it must end up. */
  carryPropId?: string;
  dropZone?: { position: Vec3Tuple; radius: number };
}

export interface LevelDef {
  id: string;
  name: string;
  /** Seeds the gameplay Rng so a replay behaves identically. */
  seed: number;
  /** Where the cat starts, and where it is returned to after a catch. */
  catBed: Vec3Tuple;
  ambientColor?: string;
  skyColor?: string;
  rooms: RoomDef[];
  geometry: GeometryDef[];
  props: PropDef[];
  granny: GrannyDef;
  hideSpots: HideSpotDef[];
  catMoments: CatMomentDef[];
  keys: KeyDef[];
  objectives: ObjectiveDef[];
  /** Keys needed to open the next level. Defaults to 2 - see DESIGN.md 1.5. */
  keysRequired?: number;
}

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

const isVec3 = (v: unknown): v is Vec3Tuple =>
  Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === 'number' && Number.isFinite(n));

const isVec2 = (v: unknown): v is Vec2Tuple =>
  Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === 'number' && Number.isFinite(n));

/**
 * Returns a list of human-readable problems. Empty means valid.
 * Written as error accumulation rather than throw-on-first so an author sees
 * every mistake in one run instead of playing whack-a-mole.
 */
export function validateLevel(data: unknown): string[] {
  const errors: string[] = [];
  const fail = (msg: string): void => void errors.push(msg);

  if (typeof data !== 'object' || data === null) return ['level is not an object'];
  const level = data as Partial<LevelDef>;

  if (!level.id) fail('missing "id"');
  if (!level.name) fail('missing "name"');
  if (typeof level.seed !== 'number') fail('missing numeric "seed"');
  if (!isVec3(level.catBed)) fail('"catBed" must be [x, y, z]');

  // Rooms
  if (!Array.isArray(level.rooms) || level.rooms.length === 0) {
    fail('"rooms" must contain at least one room');
  } else {
    for (const room of level.rooms) {
      if (!room.id) fail('a room is missing "id"');
      if (!isVec2(room.min) || !isVec2(room.max)) fail(`room "${room.id}" needs min/max [x, z]`);
      else if (room.min[0] >= room.max[0] || room.min[1] >= room.max[1]) {
        fail(`room "${room.id}" has min >= max`);
      }
    }
  }

  const ids = new Set<string>();
  const requireUniqueId = (id: string, what: string): void => {
    if (ids.has(id)) fail(`duplicate id "${id}" (${what})`);
    ids.add(id);
  };

  // Geometry
  if (!Array.isArray(level.geometry)) fail('"geometry" must be an array');
  else {
    let hasFloor = false;
    for (const geo of level.geometry) {
      if (!geo.id) fail('a geometry entry is missing "id"');
      else requireUniqueId(geo.id, 'geometry');
      if (!isVec3(geo.position))
        fail(`geometry "${geo.id}" needs "position" [x, y, z] (box CENTER)`);
      if (!isVec3(geo.size)) fail(`geometry "${geo.id}" needs "size" [x, y, z]`);
      else if (geo.size.some((s) => s <= 0)) fail(`geometry "${geo.id}" has a non-positive size`);
      if (geo.kind === 'floor') hasFloor = true;
    }
    if (!hasFloor) fail('level has no geometry of kind "floor" - the cat would fall forever');
  }

  // Props
  const propIds = new Set<string>();
  if (!Array.isArray(level.props)) fail('"props" must be an array');
  else {
    for (const prop of level.props) {
      if (!prop.id) fail('a prop is missing "id"');
      else {
        requireUniqueId(prop.id, 'prop');
        propIds.add(prop.id);
      }
      if (!prop.asset) fail(`prop "${prop.id}" is missing "asset"`);
      if (!isVec3(prop.position)) {
        fail(`prop "${prop.id}" needs "position" [x, y, z] (BASE, not center)`);
      } else if (prop.position[1] < -0.01) {
        fail(
          `prop "${prop.id}" has a negative Y (${prop.position[1]}) - props use a BASE position`,
        );
      }
      if (prop.carryable && prop.dynamic === false) {
        fail(`prop "${prop.id}" is carryable but not dynamic`);
      }
    }
  }

  // Granny
  if (!level.granny) fail('missing "granny"');
  else {
    if (!isVec3(level.granny.spawn)) fail('"granny.spawn" must be [x, y, z]');
    if (!Array.isArray(level.granny.routine) || level.granny.routine.length === 0) {
      fail('"granny.routine" needs at least one activity - otherwise she just stands there');
    } else {
      for (const act of level.granny.routine) {
        if (!act.activityId) fail('a routine entry is missing "activityId"');
        if (!isVec3(act.anchor)) fail(`activity "${act.activityId}" needs "anchor" [x, y, z]`);
        if (!(act.durationSeconds > 0)) {
          fail(`activity "${act.activityId}" needs a positive "durationSeconds"`);
        }
      }
    }
  }

  // Hide spots - without at least one, high heat is unescapable.
  if (!Array.isArray(level.hideSpots)) fail('"hideSpots" must be an array');
  else {
    if (level.hideSpots.length === 0) {
      fail('level has no hideSpots - the player would have no way to shed heat');
    }
    for (const spot of level.hideSpots) {
      if (!spot.id) fail('a hideSpot is missing "id"');
      else requireUniqueId(spot.id, 'hideSpot');
      if (!isVec3(spot.position)) fail(`hideSpot "${spot.id}" needs "position"`);
      if (!isVec3(spot.size)) fail(`hideSpot "${spot.id}" needs "size"`);
    }
  }

  // Cat Moments
  if (!Array.isArray(level.catMoments)) fail('"catMoments" must be an array');
  else {
    for (const moment of level.catMoments) {
      if (!moment.id) fail('a catMoment is missing "id"');
      else requireUniqueId(moment.id, 'catMoment');
      if (!isVec3(moment.position)) fail(`catMoment "${moment.id}" needs "position"`);
    }
  }

  // Keys
  if (!Array.isArray(level.keys)) fail('"keys" must be an array');
  else {
    for (const key of level.keys) {
      if (!key.id) fail('a key is missing "id"');
      else requireUniqueId(key.id, 'key');
      if (key.flavor === 'granny') {
        if (level.granny?.carriesKey !== key.id) {
          fail(`key "${key.id}" is flavor "granny" but granny.carriesKey does not point at it`);
        }
      } else if (!isVec3(key.position)) {
        fail(`key "${key.id}" needs a "position" (only "granny" keys may omit it)`);
      }
      if (key.flavor === 'mischief') {
        if (!key.revealedBy) fail(`key "${key.id}" is flavor "mischief" but has no "revealedBy"`);
        else if (!propIds.has(key.revealedBy)) {
          fail(`key "${key.id}" is revealedBy unknown prop "${key.revealedBy}"`);
        }
      }
    }
    const required = level.keysRequired ?? 2;
    if (level.keys.length < required) {
      fail(`keysRequired is ${required} but the level only defines ${level.keys.length} keys`);
    }
  }

  // Objectives
  if (!Array.isArray(level.objectives) || level.objectives.length === 0) {
    fail('"objectives" needs at least one entry');
  } else {
    for (const obj of level.objectives) {
      if (!obj.id) fail('an objective is missing "id"');
      else requireUniqueId(obj.id, 'objective');
      if (!obj.label) fail(`objective "${obj.id}" is missing "label"`);
      if (!(obj.target > 0)) fail(`objective "${obj.id}" needs a positive "target"`);
      if (obj.kind === 'heist') {
        if (!obj.carryPropId) fail(`heist objective "${obj.id}" needs "carryPropId"`);
        else if (!propIds.has(obj.carryPropId)) {
          fail(`heist objective "${obj.id}" references unknown prop "${obj.carryPropId}"`);
        }
        if (!obj.dropZone) fail(`heist objective "${obj.id}" needs a "dropZone"`);
      }
    }
  }

  return errors;
}

/** Throws with every problem listed at once. Used by the runtime loader. */
export function parseLevel(data: unknown, sourceName = 'level'): LevelDef {
  const errors = validateLevel(data);
  if (errors.length > 0) {
    throw new Error(`Invalid ${sourceName}:\n  - ${errors.join('\n  - ')}`);
  }
  return data as LevelDef;
}

/** Which room a point is in, or undefined if outside every room rectangle. */
export function roomIdAt(level: LevelDef, x: number, z: number): string | undefined {
  for (const room of level.rooms) {
    if (x >= room.min[0] && x <= room.max[0] && z >= room.min[1] && z <= room.max[1]) {
      return room.id;
    }
  }
  return undefined;
}
